import {
  AddTestForm,
  ConditionalOperator,
  CustomSqlCloudTestConfig,
  QualityDimension,
} from 'shared/types';

export const enforcementLevelOptions = [
  { value: 'all', label: 'All changes' },
  { value: 'breaking', label: 'Breaking changes only' },
  { value: 'downstream', label: 'Downstream impact only' },
  { value: 'downstream_critical', label: 'Critical downstream impact only' },
];

export const thresholdUnitOptions = [
  { value: 'hour', label: 'hours' },
  { value: 'day', label: 'days' },
  { value: 'week', label: 'weeks' },
  { value: 'month', label: 'months' },
];

export const DEFAULT_ANOMALY_TRAINING_PERIOD = {
  period: 'day' as const,
  count: 23,
};

export const isUnsetPeriodCount = (value: unknown): boolean =>
  value == null ||
  value === '' ||
  (typeof value === 'number' && Number.isNaN(value));

export const CUSTOM_SQL_CLOUD_ERROR = {
  STATIC_VALIDATION: 159,
  UNRESOLVABLE_RELATIONS: 160,
  ASSET_SELECTION_REQUIRED: 161,
  INVALID_PRIMARY_ASSET: 162,
  FEATURE_DISABLED: 163,
  INVALID_SCHEDULE: 164,
  NO_STATEMENT_TIMEOUT: 165,
};

export const CUSTOM_SQL_INTERVAL_HOURS = Array.from(
  { length: 23 },
  (_, i) => i + 1,
);

export const CUSTOM_CRON_FORMAT_ERROR = 'Enter a five-field cron expression';
export const CUSTOM_CRON_INVALID_ERROR = 'Invalid cron expression';
export const CUSTOM_CRON_MIN_INTERVAL_MINUTES = 15;
export const CUSTOM_CRON_SCAN_RUNS = 200;
export const MS_IN_MINUTE = 60 * 1000;

export const CUSTOM_SQL_MINUTES = Array.from({ length: 60 }, (_, i) => i);

export const DEFAULT_CLOUD_SQL_TEST_SCHEDULE = {
  scheduleType: 'daily' as const,
  intervalHours: 6,
  scheduleHour: 9,
  scheduleMinute: 0,
  scheduleDayOfWeek: 1,
};

export const CUSTOM_SQL_CONDITIONAL_OPERATORS: ConditionalOperator[] = ['>'];

export const QUALITY_DIMENSIONS: QualityDimension[] = [
  'completeness',
  'uniqueness',
  'freshness',
  'validity',
  'accuracy',
  'consistency',
];

export const NO_TIMESTAMP_COLUMN_HINT =
  'Optional. With a timestamp column, rows are counted per time bucket and past buckets are backfilled. Without one, the test counts all rows on every run, so history builds up run by run.';

export const NO_UPDATE_TIMESTAMP_COLUMN_HINT =
  'Optional. Without an updated at column, the test measures the time from the latest event to the test run.';

export const MAX_SAMPLE_ROW_LIMIT = 1000;
export const MAX_QUERY_TIMEOUT_SECONDS = 1200;

export const DEFAULT_CUSTOM_SQL_CONFIG: CustomSqlCloudTestConfig = {
  fail_row_count_threshold: 0,
  warn_row_count_threshold: null,
  store_row_sample: true,
  sample_row_limit: 100,
  query_timeout_seconds: 300,
};

export const WIZARD_UI_CONTEXT_NAME = 'test_config_wizard';

export const WIZARD_UI_CONTEXT_PROMPT =
  'The open add/edit/plan test wizard, where users add a data test, edit an existing one, or edit a planned one. Only the fields of the current step are visible. Page state: wizardMode, wizardResourceType, stage and stages (the steps for this mode, with their meaning), stageValid and stageError (why the current step is invalid), hint (what the user does next), editTestId and editedTestMetadata when editing, selection (the selected resources, testType and yamlTemplateName), testTemplates (the selectable tests with the exact testType / yamlTemplateName keys to write; do not choose an entry that has disabledReason, explain that reason instead), and timestampFieldOptions (the only values timestampColumn / updatedAtTimestampColumn accept for the selected test; allowsNone means "" is allowed for no column). The tools never move between steps or submit: the user navigates to the next step manually.';

const TIME_BUCKET_DESCRIPTION =
  'The time bucket is detectionPeriod + detectionUnit (hour, day, week, or month; no month on Redshift).';

const COMPARISON_WINDOW_DESCRIPTION =
  'The comparison window is trainingPeriodCount + trainingPeriodUnit (day, week, or month), e.g. "last 14 days" is trainingPeriodCount: 14, trainingPeriodUnit: "day". It must cover at least 7 time buckets; stageError explains why the step is invalid.';

const TIMESTAMP_COLUMN_DESCRIPTION =
  'Only values from timestampFieldOptions; "" for no column when allowsNone is true.';

const STRING_LIST_DESCRIPTION =
  'A list of strings; a comma-separated string is also accepted.';

export const WIZARD_FIELDS: Partial<
  Record<keyof AddTestForm, { label: string; description?: string }>
> = {
  resources: {
    label: 'Selected assets',
    description:
      "Ids of tables the wizard lists. get_table_assets also returns assets the wizard does not list: Elementary's own package models (ids containing model.elementary. or a schema ending in _elementary), seeds, snapshots, and assets that cannot have tests. Never choose those as targets, even when the user asks for random or all tables; pick from the user's own models and sources instead.",
  },
  resourceType: { label: 'Asset type' },
  testType: {
    label: 'Test',
    description: 'The testType of a testTemplates entry.',
  },
  yamlTemplateName: {
    label: 'YAML test',
    description:
      'The yamlTemplateName of a testTemplates entry when testType is yamlTest.',
  },
  customQuery: { label: 'SQL query' },
  testName: { label: 'Test name' },
  description: { label: 'Description' },
  owners: { label: 'Owners', description: STRING_LIST_DESCRIPTION },
  tags: { label: 'Tags', description: STRING_LIST_DESCRIPTION },
  qualityDimension: { label: 'Quality dimension' },
  yaml: {
    label: 'Configuration',
    description:
      'Only the test parameters, such as combination_of_columns and quote_columns. Set severity, owners, tags, shouldStoreFailures, description, whereClause, and testNameOverride as their own fields, and do not put store_failures, tags, meta, error_if, or warn_if under config. error_if / warn_if found in the YAML are moved to severity.',
  },
  severity: {
    label: 'Severity',
    description:
      'error or warn, or conditional: { type: "error" or "warn", conditionalError: { operator: ">", value: 1 }, conditionalWarning?: { operator, value } }. type may be "conditional", and errorIf / warnIf strings such as ">1" are accepted inside severity.',
  },
  timestampColumn: {
    label: 'Timestamp column',
    description: TIMESTAMP_COLUMN_DESCRIPTION,
  },
  updatedAtTimestampColumn: {
    label: 'Updated at column',
    description: TIMESTAMP_COLUMN_DESCRIPTION,
  },
  detectionPeriod: {
    label: 'Detection period',
    description: TIME_BUCKET_DESCRIPTION,
  },
  detectionUnit: {
    label: 'Detection period unit',
    description: TIME_BUCKET_DESCRIPTION,
  },
  trainingPeriodCount: {
    label: 'Training period',
    description: COMPARISON_WINDOW_DESCRIPTION,
  },
  trainingPeriodUnit: {
    label: 'Training period unit',
    description: COMPARISON_WINDOW_DESCRIPTION,
  },
  backfillDays: { label: 'Backfill days' },
  detectionEventType: { label: 'Detection event type' },
  seasonality: { label: 'Seasonality' },
  sensitivity: { label: 'Sensitivity' },
  whereClause: { label: 'Where' },
  columnGroup: { label: 'Columns', description: 'A list of column names.' },
  anomalyConfig: {
    label: 'Detection settings',
    description: 'An object; only the keys you pass are changed.',
  },
  baselineMode: { label: 'Baseline' },
  enforcementLevel: { label: 'Enforcement level' },
  baselineColumns: {
    label: 'Expected columns',
    description: 'A list of { column_name, data_type, is_nullable } objects.',
  },
  values: { label: 'Values', description: 'A list of accepted values.' },
  valuesAreNotStrings: { label: 'Values are not strings' },
  columnName: { label: 'Referenced column' },
  tableName: { label: 'Referenced table' },
  limit: { label: 'Limit' },
  shouldStoreFailures: { label: 'Store failures' },
  testNameOverride: { label: 'Name override' },
  scheduleType: { label: 'Schedule' },
  intervalHours: { label: 'Every (hours)' },
  scheduleHour: { label: 'Hour' },
  scheduleMinute: { label: 'Minute' },
  scheduleDayOfWeek: { label: 'Day of week' },
  scheduleCron: { label: 'Cron expression' },
  storeRowSample: { label: 'Store row sample' },
  sampleRowLimit: { label: 'Sample row limit' },
  queryTimeoutSeconds: { label: 'Query timeout (seconds)' },
  customSqlPrimaryAssetId: { label: 'Tested table' },
  path: { label: 'Path' },
  testedTable: { label: 'Tested table' },
};

export const WIZARD_TARGET_FIELD_NAMES = ['resources', 'resourceType'];

export const WIZARD_TEST_FIELD_NAMES = ['testType', 'yamlTemplateName'];

export const WIZARD_COMMON_CONFIG_FIELD_NAMES = [
  'description',
  'owners',
  'tags',
  'severity',
];

const OSS_ANOMALY_CONFIG_FIELD_NAMES = [
  'timestampColumn',
  'backfillDays',
  'detectionPeriod',
  'detectionUnit',
  'seasonality',
  'sensitivity',
  'trainingPeriodCount',
  'trainingPeriodUnit',
  'whereClause',
];

export const WIZARD_CONFIG_FIELD_NAMES_BY_TEST_TYPE: Record<string, string[]> =
  {
    freshnessAnomalies: OSS_ANOMALY_CONFIG_FIELD_NAMES,
    eventFreshnessAnomalies: [
      ...OSS_ANOMALY_CONFIG_FIELD_NAMES,
      'updatedAtTimestampColumn',
    ],
    volumeAnomalies: [...OSS_ANOMALY_CONFIG_FIELD_NAMES, 'detectionEventType'],
    dimensionAnomalies: [
      ...OSS_ANOMALY_CONFIG_FIELD_NAMES,
      'detectionEventType',
      'columnGroup',
    ],
    cloudFreshnessAnomalies: ['anomalyConfig'],
    cloudVolumeAnomalies: ['anomalyConfig'],
    cloudPipelineTaskPerformance: ['anomalyConfig'],
    cloudDataContract: ['baselineMode', 'enforcementLevel', 'baselineColumns'],
    acceptedValues: ['values', 'valuesAreNotStrings'],
    relationship: ['columnName', 'tableName'],
    yamlTest: [
      'yaml',
      'whereClause',
      'limit',
      'shouldStoreFailures',
      'testNameOverride',
    ],
    customQuery: [
      'customQuery',
      'testName',
      'qualityDimension',
      'scheduleType',
      'intervalHours',
      'scheduleHour',
      'scheduleMinute',
      'scheduleDayOfWeek',
      'scheduleCron',
      'storeRowSample',
      'sampleRowLimit',
      'queryTimeoutSeconds',
      'customSqlPrimaryAssetId',
      'path',
      'testedTable',
    ],
  };
